import React, { useState, useCallback, useEffect } from "react";
import {
    StaticDatePicker,
    PickersDay,
    PickersDayProps,
} from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Box, Button, Stack, Grid, Paper, Typography } from "@mui/material";
import WSTimePicker from "../../Components/WSTimePicker/WSTimePicker";
import { useForm } from "react-hook-form";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { useNavigate, useParams } from "react-router-dom";
import { breadCrumbsStore } from "../../Components/BreadCrumbs/BreadCrumbsListner";
import http from "../../commonFunctions/httpClient";

type Slot = {
    startTime: string;
    endTime: string;
    isAvailable: boolean;
};

interface Schedule {
    [date: string]: Slot[];
}

interface DateScheduleProps {

    onChange?: (payload: any) => void;
}

const DateScheduleCreate: React.FC<DateScheduleProps> = ({

    onChange,
}) => {
    const [selectedDates, setSelectedDates] = useState<Dayjs[]>([]);
    const [rangeStart, setRangeStart] = useState<Dayjs | null>(null);
    const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
    const [schedule, setSchedule] = useState<Schedule>({});
    const [startTime, setStartTime] = useState<string>("");
    const [doctorProfileId, setDoctorProfileId] = useState<string>("");
    const params = useParams()
    const id = params?.userId
    const [endTime, setEndTime] = useState<string>("");
    const navigate = useNavigate();
    const { showMessage } = useSnackbar();
    const {
        register,
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        getValues,
    } = useForm();
    useEffect(() => {
        /**
         * Method to get the user record by id for edit purpose
         * @param id
         */
        async function getDoctorById(id: any): Promise<void> {
            try {

                
                const response: any = await http.get(`DoctorProfile/User/${id}`);
                if (response && response.data) {
                    setDoctorProfileId(response.data?.id)


                }

            } catch (error) {
                showMessage("Error fetching schedule:", "error");
            } finally {

            }

         
        }
           if (id) {
                getDoctorById(id);
            }
    }, [id]);
    const isSameDay = (a: Dayjs, b: Dayjs) => a.isSame(b, "day");

    const isDateSelected = useCallback(
        (date: Dayjs) => selectedDates.some((d) => isSameDay(d, date)),
        [selectedDates]
    );
    breadCrumbsStore.updateBreadCrumbs([
        { "Doctors": `/contact/list/${0}` },
        { ["Update Schedule "]: "" },
    ]);

    const generateSlots = (start: string, end: string): Slot[] => {
        if (!start || !end) return [];
        const slots: Slot[] = [];

        let current = dayjs(start, "HH:mm");
        const endTimeObj = dayjs(end, "HH:mm");

        while (current.isBefore(endTimeObj)) {
            const slotEnd = current.add(15, "minute");
            if (slotEnd.isAfter(endTimeObj)) break;

            slots.push({
                startTime: current.format("H:mm"),
                endTime: slotEnd.format("H:mm"),
                isAvailable: true,
            });

            current = slotEnd;
        }

        return slots;
    };

    const updateScheduleSlots = (dates: Dayjs[], start: string, end: string) => {
        if (!start || !end) return {};
        const newSchedule: Schedule = {};
        dates.forEach((date) => {
            newSchedule[date.format("YYYY-MM-DD")] = generateSlots(start, end);
        });
        return newSchedule;
    };

    const toggleDate = useCallback(
        (date: Dayjs) => {
            const dateKey = date.format("YYYY-MM-DD");
            if (isDateSelected(date)) {
                setSelectedDates((prev) => prev.filter((d) => !isSameDay(d, date)));
                setSchedule((prev) => {
                    const newSchedule = { ...prev };
                    delete newSchedule[dateKey];
                    return newSchedule;
                });
            } else {
                setSelectedDates((prev) => [...prev, date]);
                setSchedule((prev) => ({
                    ...prev,
                    [dateKey]: generateSlots(startTime, endTime),
                }));
            }
        },
        [isDateSelected, startTime, endTime]
    );

    const handleDateClick = (date: Dayjs) => {
        if (isDateSelected(date)) {
            toggleDate(date);
            return;
        }

        if (!rangeStart) {
            setRangeStart(date);
            toggleDate(date);
        } else {
            const start = rangeStart.isBefore(date) ? rangeStart : date;
            const end = rangeStart.isAfter(date) ? rangeStart : date;

            const newDates: Dayjs[] = [];
            let current = start;
            while (current.isBefore(end) || current.isSame(end, "day")) {
                if (!isDateSelected(current)) {
                    newDates.push(current);
                }
                current = current.add(1, "day");
            }

            setSelectedDates((prev) => [...prev, ...newDates]);
            setSchedule((prev) => {
                const updated = { ...prev };
                newDates.forEach((d) => {
                    updated[d.format("YYYY-MM-DD")] = generateSlots(startTime, endTime);
                });
                return updated;
            });

            setRangeStart(null);
        }
    };

    const handleSelectAll = () => {
        const daysInMonth = currentMonth.daysInMonth();
        const allDates = Array.from({ length: daysInMonth }, (_, i) =>
            dayjs(currentMonth.format("YYYY-MM")).date(i + 1)
        );
        setSelectedDates(allDates);
        setSchedule(() => {
            const newSchedule: Schedule = {};
            allDates.forEach((d) => {
                newSchedule[d.format("YYYY-MM-DD")] = generateSlots(startTime, endTime);
            });
            return newSchedule;
        });
    };

    const handleUnselectAll = () => {
        setSelectedDates([]);
        setRangeStart(null);
        setSchedule({});
    };

    useEffect(() => {
        if (startTime && endTime && selectedDates.length) {
            setSchedule(updateScheduleSlots(selectedDates, startTime, endTime));
        }
    }, [startTime, endTime, selectedDates]);

    const CustomDay = React.memo((props: PickersDayProps<Dayjs>) => {
        const { day, outsideCurrentMonth, ...other } = props;
        const selected = isDateSelected(day);

        return (
            <PickersDay
                {...other}
                day={day}
                outsideCurrentMonth={outsideCurrentMonth}
                selected={selected}
                onFocus={(e) => e.stopPropagation()}
                onClick={() => handleDateClick(day)}
                sx={{
                    bgcolor: selected ? "primary.main" : undefined,
                    color: selected ? "white" : undefined,
                    cursor: "pointer",
                }}
            />
        );
    });

    useEffect(() => {
        if (onChange) {
            const payload = [
                {
                    doctorProfileId,
                    days: selectedDates.map((d) => ({
                        day: d.toDate().toISOString(),
                        slots: schedule[d.format("YYYY-MM-DD")] || [],
                    })),
                },
            ];
            onChange(payload);
        }
    }, [selectedDates, schedule, onChange, doctorProfileId]);

    // New submit handler
    const onSubmit = async () => {
        const payload = [
            {
                doctorProfileId,
                days: selectedDates.map((d) => ({
                    day: d.startOf("day").toDate().toISOString(),
                    slots: (schedule[d.format("YYYY-MM-DD")] || []).map((slot) => ({
                        startTime: dayjs(slot.startTime, "H:mm").format("HH:mm"), // 24-hour format
                        endTime: dayjs(slot.endTime, "H:mm").format("HH:mm"),     // 24-hour format
                        isAvailable: slot.isAvailable,
                    })),
                })),
            },
        ];


        try {

            const response = await callAPI(`DoctorSchedule`, Method.POST, payload)
            if (response && response.success) {
                showMessage(response?.message || 'Schedule created', 'success')
                response.success === true && navigate("/contact/list");
            } else {
                showMessage(response?.message || response?.error || 'Schedule not created', 'error')
            }

        } catch (error: any) {
            showMessage(error?.message, 'error')
        }
        // do whatever you want with payload here
    };


    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Paper>
                <Grid container spacing={2} mt={0} ml={0}>
                    <Grid item sm={12} xs={12}>
                        <Typography sx={{ fontSize: "0.875rem", fontWeight: "bold" }}>
                            Schedule{" "}
                        </Typography>
                    </Grid>
                </Grid>
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Button
                            sx={{ fontSize: "0.75rem" }}
                            variant="outlined"
                            onClick={handleSelectAll}
                            size="small"
                        >
                            Select All
                        </Button>
                        <Button
                            sx={{ fontSize: "0.75rem" }}
                            variant="outlined"
                            color="error"
                            onClick={handleUnselectAll}
                            size="small"
                        >
                            Unselect All
                        </Button>
                    </Stack>

                    <StaticDatePicker
                        displayStaticWrapperAs="desktop"
                        value={null}
                        onChange={() => { }}
                        onMonthChange={(newMonth) => setCurrentMonth(newMonth)}
                        slots={{ day: CustomDay }}
                    />

                    <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item sm={6}>
                            <WSTimePicker
                                control={control}
                                rules={{ required: true }}
                                id="startTime"
                                label="Start Time"
                                ampm={false}
                                name="startTime"
                                onChange={(val: string) => setStartTime(val)}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <WSTimePicker
                                control={control}
                                rules={{ required: true }}
                                id="endTime"
                                label="End Time"
                                ampm={false}
                                name="endTime"
                                onChange={(val: string) => setEndTime(val)}
                            />
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 3 }}>
                        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mb: 2 }}>
                            <Button
                                variant="contained"
                                size="small"
                                sx={{
                                    borderRadius: 8,
                                    width: "8rem",
                                    border: "1px solid #098963",
                                    color: "#FFFFFF",
                                    backgroundColor: "#098963",
                                    "&:hover": {
                                        backgroundColor: "#066e4f",
                                    },
                                }}
                                onClick={handleSubmit(onSubmit)}
                                disabled={!startTime || !endTime || selectedDates.length === 0}
                            >
                                Save
                            </Button>
                        </Stack>

                    </Box>
                </Box>
            </Paper>
        </LocalizationProvider>
    );
};

export default DateScheduleCreate;
